import {
  AddCircleOutline,
  CheckCircle,
  OpenInNew,
  RateReview,
} from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import type { AlertColor } from "@mui/material/Alert";
import { isAxiosError } from "axios";
import { useEffect, useState } from "react";

import { getAdminAppUrl } from "../../../api/apiConfig";
import type QueryClient from "../../../api/query/queryClient";
import type { VerificationEvalCase } from "../../../api/query/queryClient";
import useDevMode from "../../../utils/devmode";

interface EvalCaseActionsProps {
  wieldyId: string;
  hasFile: boolean;
  queryClient: QueryClient;
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
}

const STATUS_STYLE = {
  REVIEW: {
    hint: "In review",
    color: "warning",
    icon: <RateReview sx={{ fontSize: 14 }} />,
  },
  ACCEPTED: {
    hint: "Accepted",
    color: "success",
    icon: <CheckCircle sx={{ fontSize: 14 }} />,
  },
} as const;

const BUTTON_SX = {
  textTransform: "none",
  fontWeight: 500,
  fontSize: "0.8125rem",
  lineHeight: 1.5,
  px: 1.25,
  py: 0.25,
  mr: 1,
  border: "1px solid transparent",
  borderRadius: 1,
} as const;

function adminCaseUrl(caseId: string): string {
  return `${getAdminAppUrl()}/evals/cases/${caseId}`;
}

function errorMessage(error: unknown): string {
  if (isAxiosError(error) && typeof error.response?.data?.detail === "string") {
    return error.response.data.detail;
  }
  return "Could not update the eval case";
}

function addTooltip(hasFile: boolean, status: string | null): string {
  if (status === "EXCLUDED") {
    return "Removed earlier. Adding brings it back for review.";
  }
  return hasFile ? "" : "Needs a Benny PDF";
}

function EvalCaseActions({
  wieldyId,
  hasFile,
  queryClient,
  updateSnackBar,
}: EvalCaseActionsProps) {
  const devMode = useDevMode();
  const [evalCase, setEvalCase] = useState<VerificationEvalCase | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!devMode) return undefined;
    let cancelled = false;
    queryClient
      .getVerificationEvalCase(wieldyId)
      .then((result) => !cancelled && setEvalCase(result))
      .catch(() => !cancelled && setEvalCase(null));
    return () => {
      cancelled = true;
    };
  }, [devMode, wieldyId, queryClient]);

  if (!devMode || !evalCase?.canFlag) return null;

  const handleConfirm = async () => {
    const tab = window.open("", "_blank");
    setSaving(true);
    try {
      const result = await queryClient.addVerificationEvalCase(wieldyId);
      setEvalCase(result);
      if (tab && result.caseId) {
        tab.location.href = adminCaseUrl(result.caseId);
      }
    } catch (error) {
      tab?.close();
      updateSnackBar({ severity: "error", message: errorMessage(error) });
    } finally {
      setSaving(false);
      setConfirming(false);
    }
  };

  const activeStyle =
    evalCase.status === "REVIEW" || evalCase.status === "ACCEPTED"
      ? STATUS_STYLE[evalCase.status]
      : null;

  return (
    <>
      {activeStyle && evalCase.caseId ? (
        <Tooltip title={activeStyle.hint}>
          <Button
            size="small"
            variant="contained"
            color={activeStyle.color}
            disableElevation
            href={adminCaseUrl(evalCase.caseId)}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={activeStyle.icon}
            endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
            data-testid="viewEvalCase"
            sx={BUTTON_SX}
          >
            View Eval Case
          </Button>
        </Tooltip>
      ) : (
        <Tooltip title={addTooltip(hasFile, evalCase.status)}>
          <span>
            <Button
              size="small"
              variant="contained"
              disableElevation
              startIcon={<AddCircleOutline sx={{ fontSize: 14 }} />}
              disabled={!hasFile && evalCase.status !== "EXCLUDED"}
              onClick={() => setConfirming(true)}
              data-testid="addEvalCase"
              sx={BUTTON_SX}
            >
              Add as Eval Case
            </Button>
          </span>
        </Tooltip>
      )}

      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>Add as Eval Case?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Future prompt changes are tested against this verification. You can
            review the case next.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={saving}
            data-testid="confirmEvalCase"
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default EvalCaseActions;
